import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, Loader2, PieChart as PieIcon } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { STATUS_LABEL } from "./ApplicationsList";
import { toast } from "sonner";

const STATUS_COLOR = {
  submetida: "#94a3b8",   // slate-400
  em_analise: "#f59e0b",  // amber-500
  entrevista: "#3b82f6",  // blue-500
  aceite: "#10b981",      // emerald-500
  rejeitada: "#ef4444",   // red-500
};

export function ApplicationsChart({ weeks = 8 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/candidate/stats/timeline?weeks=${weeks}`);
        setData(data);
      } catch (e) {
        toast.error(formatErr(e));
      } finally { setLoading(false); }
    })();
  }, [weeks]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 flex items-center justify-center h-56">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) return null;

  const total = data.total || 0;
  const maxBar = Math.max(1, ...data.weeks.map((w) => w.count));
  const statusEntries = Object.entries(data.status_counts).filter(([, v]) => v > 0);

  return (
    <section
      data-testid="applications-chart"
      className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center">
            <TrendingUp className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-heading font-black tracking-tighter text-lg text-slate-900">O teu progresso</h2>
            <p className="text-xs text-slate-500">Candidaturas por semana e distribuição por estado</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-heading font-black tracking-tighter text-2xl text-slate-900 leading-none">{total}</p>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mt-1">Total</p>
        </div>
      </div>

      {total === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Ainda não te candidataste a nenhuma vaga. Começa agora!
        </div>
      ) : (
        <>
          {/* Bar chart */}
          <div data-testid="weekly-chart" className="h-40 -ml-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weeks} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, Math.max(4, maxBar + 1)]}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={24}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "#f1f5f9" }}
                  contentStyle={{
                    background: "#0f172a", border: "none", borderRadius: 12,
                    color: "white", fontSize: 12, padding: "8px 12px",
                  }}
                  labelStyle={{ color: "#cbd5e1", fontWeight: 600 }}
                  formatter={(v) => [v, "candidaturas"]}
                />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {data.weeks.map((w) => (
                    <Cell key={`cell-${w.label || w.iso || w.count}`} fill={w.count > 0 ? "#f59e0b" : "#e2e8f0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Progress bar por estado */}
          {statusEntries.length > 0 && (
            <div data-testid="status-bar" className="mt-4 pt-4 border-t border-black/5">
              <div className="flex items-center gap-2 mb-2">
                <PieIcon className="w-3.5 h-3.5 text-slate-400" />
                <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Distribuição por estado</p>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                {statusEntries.map(([k, v]) => {
                  const pct = (v / total) * 100;
                  return (
                    <div key={k}
                      className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: STATUS_COLOR[k] || "#94a3b8" }}
                      title={`${STATUS_LABEL[k] || k}: ${v}`}
                    />
                  );
                })}
              </div>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-3">
                {statusEntries.map(([k, v]) => (
                  <li key={k} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLOR[k] || "#94a3b8" }} />
                    <span className="text-slate-600 flex-1">{STATUS_LABEL[k] || k}</span>
                    <span className="font-bold text-slate-900">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
